/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { PrimaryButton, Toggle, Stack, MessageBar, MessageBarType } from "@fluentui/react";
import useAxios from "axios-hooks";
import Toast from "../../../Forms/Toast";
import _ from "lodash";

// Override the API-supplied report names so the admin matches the labels
// shown in the client console UI (REPORT_GROUPS in the React app).
// Keys are the camel-cased InternalName from dbo.ClientConsoleReports.
// Any key not present here falls back to the API-supplied Name.
const REPORT_NAME_OVERRIDES = {
    dashboard: "Impact",
    usageSummary: "Activity Summary (Aggregate)",
    userIngressAggregate: "User Entry Points (Aggregate)",
    userRegistrations: "User Registrations (User Detail)",
    userLogins: "User Logins (User Detail)",
    userCallsToActionAggregate: "Calls to Action (Aggregate)",
    userCallsToAction: "Calls to Action (User Detail)",
    userCreditAggregate: "Credit Score & Report (Aggregate)",
    userCreditEnrollment: "Credit Score & Report (User Detail)",
    userSearches: "Search Activity (User Detail)",
    userCheckupsAggregate: "Financial Checkup Activity (Aggregate)",
    userCheckups: "Financial Checkup Activity (User Detail)",
    userFinancialWellnessChange: "Financial Checkup Results (User Detail)",
    userStressAggregate: "Financial Stress Score (Aggregate)",
    userStressChange: "Financial Stress Score (User Detail)",
    userMoneyPersonalityAggregate: "Your Money Personality \u00AE (Aggregate)",
    userMoneyPersonality: "Your Money Personality \u00AE (User Detail)",
    userAiAggregate: "Conversational AI (Aggregate)",
    userAiConversations: "Conversational AI (User Detail)",
    userCoursesAggregate: "Courses (Aggregate)",
    userCourses: "Courses (User Detail)",
    userResources: "Content and Resources (User Detail)",
    userGamificationAggregate: "Gamification (Aggregate)",
    userXp: "Experience Points (XP) (User Detail)",
    userSproutsEarned: "Sprouts / Coins Earned (User Detail)",
    userSproutsRedeemed: "Sprouts / Coins Redeemed (User Detail)",
    userMindfulnessAggregate: "Money Mindfulness (Aggregate)",
    userMeditations: "Meditations (User Detail)",
    userJournals: "Journaling (User Detail)",
    userMoneyMindfulness: "8-Week Program (User Detail)",
};

const ClientConsoleReportsOnOffSettingsTable = ({ cobrandList }) => {
    const [showToast, setShowToast] = useState(false);

    // Settings for the cobrands
    const [settings, setSettings] = useState(null);

    const [{ loading: loadingSettings }, fetchSettings] = useAxios(
        { url: "/internal/cobrand/client-console-reports-on-off/0" },
        { manual: true }
    );

    const [{ loading: saving }, saveSettings] = useAxios(
        { url: "/internal/cobrand/client-console-reports-on-off-batch", method: "POST" },
        { manual: true }
    );

    // Helper: get the list of boolean keys from current settings (exclude cobrandId)
    const booleanKeys = useMemo(() => {
        if (!settings) return [];
        return Object.keys(settings).filter((k) => k !== "cobrandId" && typeof settings[k] === "boolean");
    }, [settings]);

    // Load settings for a single cobrand or use first cobrand's settings for multiple
    const loadSettings = useCallback(async () => {
        if (!cobrandList || cobrandList.length === 0) {
            setSettings(null);
            return;
        }

        // Always load settings from the first cobrand
        const cobrandId = cobrandList[0].coBrandId;
        const result = await fetchSettings({
            url: `/internal/cobrand/client-console-reports-on-off/${cobrandId}`,
        });
        if (result && result.data) {
            const incoming = result.data;
            // Remove cobrandId from settings - cobrandList is the source of truth
            const { cobrandId: _, ...settingsWithoutCobrandId } = incoming;
            setSettings(settingsWithoutCobrandId);
        }
    }, [cobrandList, fetchSettings]);

    // Load settings when cobrandList changes
    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // Submit/save current settings
    const handleSubmit = async () => {
        if (!cobrandList || cobrandList.length === 0 || !settings) return;

        const payload = {
            cobrandIds: cobrandList.map((x) => x.coBrandId),
            settings,
        };

        const result = await saveSettings({ data: payload });
        if (result && result.data) {
            setShowToast(true);
        }
    };

    const allChecked = booleanKeys.length > 0 && booleanKeys.every((k) => !!settings?.[k]);

    // reports meta data
    const [{ data: reportsMetaData, loading: loadingMeta }] = useAxios(
        { url: "/internal/cobrand/client-console-all-reports-meta" },
        { manual: false }
    );

    // Create a lookup to go from the [api modified] InternalName to the friendly name
    const byInternalNameLookup = useMemo(() => {
        if (!reportsMetaData) return {};

        // Tweak the internal name to match how the api modified it in the settings GET request
        const modified = reportsMetaData.map((x) => ({
            ...x,
            internalNameMod: x.internalName.charAt(0).toLowerCase() + x.internalName.slice(1),
        }));

        return _.keyBy(modified, "internalNameMod");
    }, [reportsMetaData]);

    // Guards
    if (!cobrandList || cobrandList.length === 0) {
        return <div>No cobrands provided</div>;
    }

    if (loadingSettings || loadingMeta) {
        return <div>Loading...</div>;
    }

    return (
        <Formik initialValues={{}} onSubmit={handleSubmit}>
            {({ submitForm }) => (
                <Form style={{ width: 400 }}>
                    <div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 10,
                            }}
                        >
                            <h4 style={{ margin: 0 }}>Reports</h4>
                            <Toggle
                                label="Toggle All"
                                checked={allChecked}
                                onChange={(_, checked) => {
                                    const updated = {};
                                    booleanKeys.forEach((k) => (updated[k] = !!checked));
                                    setSettings((s) => ({ ...(s || {}), ...updated }));
                                }}
                            />
                        </div>

                        <Stack tokens={{ childrenGap: 8 }}>
                            {booleanKeys.map((key) => (
                                <Toggle
                                    key={key}
                                    label={
                                        <>
                                            <span>{REPORT_NAME_OVERRIDES[key] ?? byInternalNameLookup[key]?.name}</span>
                                            {!byInternalNameLookup[key].isEnabled && (
                                                <span
                                                    style={{
                                                        marginLeft: 10,
                                                        fontWeight: "lighter",
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    *Currently disabled globally
                                                </span>
                                            )}
                                        </>
                                    }
                                    checked={!!settings?.[key]}
                                    onChange={(_, checked) => setSettings((s) => ({ ...(s || {}), [key]: !!checked }))}
                                />
                            ))}
                        </Stack>

                        <div style={{ marginTop: 16 }}>
                            <PrimaryButton
                                type="button"
                                text={saving ? "Saving..." : "Save"}
                                disabled={saving || loadingSettings || !settings}
                                onClick={submitForm}
                            />{" "}
                        </div>

                        {cobrandList.length > 1 && (
                            <div className="mt-3">
                                <MessageBar messageBarType={MessageBarType.info}>
                                    <div>
                                        Defaulted to settings for <strong>{cobrandList[0].name}</strong>.
                                    </div>
                                    <div>
                                        Saving will affect all selected <strong>({cobrandList.length})</strong>{" "}
                                        cobrands.
                                    </div>
                                </MessageBar>
                            </div>
                        )}
                    </div>

                    <Toast show={showToast} message="Successfully Saved" onClose={() => setShowToast(false)} />
                </Form>
            )}
        </Formik>
    );
};

ClientConsoleReportsOnOffSettingsTable.propTypes = {
    cobrandList: PropTypes.arrayOf(
        PropTypes.shape({
            coBrandId: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default ClientConsoleReportsOnOffSettingsTable;
